import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Plus, Building2, DollarSign, TrendingUp,
  PieChart, Trash2, Edit2, X, Save, Home, Briefcase
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip, Legend
} from "recharts";

const PortfolioTracker = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  
  const [formData, setFormData] = useState({
    property_name: "",
    address: "",
    property_type: "single_family",
    status: "owned",
    purchase_date: "",
    purchase_price: 0,
    current_value: 0,
    loan_balance: 0,
    monthly_rent: 0,
    monthly_expenses: 0,
    monthly_mortgage: 0,
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [propsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/portfolio/properties`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/portfolio/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setProperties(propsRes.data.properties || []);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error("Failed to load portfolio data");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      property_name: "",
      address: "",
      property_type: "single_family",
      status: "owned",
      purchase_date: "",
      purchase_price: 0,
      current_value: 0,
      loan_balance: 0,
      monthly_rent: 0,
      monthly_expenses: 0,
      monthly_mortgage: 0,
      notes: ""
    });
  };

  const handleSaveProperty = async () => {
    if (!formData.property_name) {
      toast.error("Please enter a property name");
      return;
    }

    try {
      if (editingProperty) {
        await axios.put(`${API}/portfolio/properties/${editingProperty.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Property updated!");
      } else {
        await axios.post(`${API}/portfolio/properties`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Property added!");
      }
      setShowAddDialog(false);
      setEditingProperty(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save property");
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      property_name: property.property_name,
      address: property.address,
      property_type: property.property_type,
      status: property.status,
      purchase_date: property.purchase_date,
      purchase_price: property.purchase_price,
      current_value: property.current_value,
      loan_balance: property.loan_balance,
      monthly_rent: property.monthly_rent,
      monthly_expenses: property.monthly_expenses,
      monthly_mortgage: property.monthly_mortgage,
      notes: property.notes
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (propertyId) => {
    if (!confirm("Are you sure you want to delete this property?")) return;
    
    try {
      await axios.delete(`${API}/portfolio/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Property deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

  const COLORS = ['#06b6d4', '#F59E0B', '#10b981', '#8b5cf6', '#ef4444', '#ec4899'];

  const pieData = summary?.properties_by_type ? 
    Object.entries(summary.properties_by_type).map(([type, data], index) => ({
      name: type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: data.value,
      count: data.count,
      color: COLORS[index % COLORS.length]
    })) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="portfolio-tracker-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">Portfolio Tracker</h1>
            </div>
            <Button 
              onClick={() => {
                resetForm();
                setEditingProperty(null);
                setShowAddDialog(true);
              }}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              data-testid="add-property-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Property
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Portfolio Summary */}
        {summary && summary.total_properties > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Portfolio Summary</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="metric-card" data-testid="total-properties">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-5 w-5 text-amber-500" />
                  <span className="text-sm text-slate-400">Properties</span>
                </div>
                <div className="text-3xl font-bold text-white font-mono">{summary.total_properties}</div>
              </div>
              <div className="metric-card" data-testid="total-value">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-5 w-5 text-cyan-500" />
                  <span className="text-sm text-slate-400">Total Value</span>
                </div>
                <div className="text-2xl font-bold text-white font-mono">{formatCurrency(summary.total_value)}</div>
              </div>
              <div className="metric-card" data-testid="total-equity">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                  <span className="text-sm text-slate-400">Total Equity</span>
                </div>
                <div className="text-2xl font-bold text-emerald-500 font-mono">{formatCurrency(summary.total_equity)}</div>
              </div>
              <div className="metric-card" data-testid="monthly-cashflow">
                <div className="flex items-center gap-2 mb-2">
                  <Briefcase className="h-5 w-5 text-purple-500" />
                  <span className="text-sm text-slate-400">Monthly Cash Flow</span>
                </div>
                <div className={`text-2xl font-bold font-mono ${summary.monthly_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {formatCurrency(summary.monthly_cash_flow)}
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Additional Metrics */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Financial Metrics</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Total Debt</span>
                    <span className="font-mono text-white">{formatCurrency(summary.total_debt)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">LTV Ratio</span>
                    <span className="font-mono text-white">{formatPercent(summary.ltv_ratio)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Monthly Rent</span>
                    <span className="font-mono text-emerald-500">{formatCurrency(summary.monthly_rent)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Monthly Expenses</span>
                    <span className="font-mono text-red-400">{formatCurrency(summary.monthly_expenses)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Annual Cash Flow</span>
                    <span className={`font-mono font-semibold ${summary.annual_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {formatCurrency(summary.annual_cash_flow)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-800">
                    <span className="text-slate-400">Avg Cap Rate</span>
                    <span className="font-mono text-cyan-500">{formatPercent(summary.average_cap_rate)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-slate-400">Avg Cash-on-Cash</span>
                    <span className="font-mono text-amber-500">{formatPercent(summary.average_coc)}</span>
                  </div>
                </div>
              </div>

              {/* Pie Chart */}
              {pieData.length > 0 && (
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Portfolio Allocation</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend 
                          formatter={(value, entry) => (
                            <span className="text-slate-300 text-sm">{value}</span>
                          )}
                        />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Properties List */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4">Properties</h2>
          {properties.length === 0 ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <Building2 className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Properties Yet</h3>
              <p className="text-slate-400 mb-6">Add your first property to start tracking your portfolio</p>
              <Button 
                onClick={() => setShowAddDialog(true)}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Property
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {properties.map((property) => (
                <div key={property.id} className="glass-card rounded-xl p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-white">{property.property_name}</h3>
                      <p className="text-sm text-slate-400">{property.address || 'No address'}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      property.status === 'owned' ? 'bg-emerald-500/20 text-emerald-500' :
                      property.status === 'under_contract' ? 'bg-amber-500/20 text-amber-500' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {property.status.replace('_', ' ')}
                    </span>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Value</span>
                      <span className="font-mono text-white">{formatCurrency(property.current_value)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Equity</span>
                      <span className="font-mono text-emerald-500">{formatCurrency(property.equity)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Cash Flow</span>
                      <span className={`font-mono ${property.monthly_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {formatCurrency(property.monthly_cash_flow)}/mo
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(property)}
                      className="flex-1 border-slate-700 text-slate-300"
                    >
                      <Edit2 className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(property.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Property Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          setEditingProperty(null);
          resetForm();
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingProperty ? 'Edit Property' : 'Add Property'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingProperty ? 'Update property details' : 'Add a property to your portfolio'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Property Name *</Label>
              <Input
                value={formData.property_name}
                onChange={(e) => setFormData({ ...formData, property_name: e.target.value })}
                placeholder="123 Main Street"
                className="bg-slate-950 border-slate-800 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="City, State"
                className="bg-slate-950 border-slate-800 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Property Type</Label>
                <Select 
                  value={formData.property_type} 
                  onValueChange={(v) => setFormData({ ...formData, property_type: v })}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="single_family">Single Family</SelectItem>
                    <SelectItem value="duplex">Duplex</SelectItem>
                    <SelectItem value="triplex">Triplex</SelectItem>
                    <SelectItem value="fourplex">Fourplex</SelectItem>
                    <SelectItem value="multifamily">Multi-Family</SelectItem>
                    <SelectItem value="condo">Condo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="owned">Owned</SelectItem>
                    <SelectItem value="under_contract">Under Contract</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Purchase Price</Label>
                <Input
                  type="number"
                  value={formData.purchase_price}
                  onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-950 border-slate-800 text-white font-mono"
                />
              </div>
              <div>
                <Label className="text-slate-300">Current Value</Label>
                <Input
                  type="number"
                  value={formData.current_value}
                  onChange={(e) => setFormData({ ...formData, current_value: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-950 border-slate-800 text-white font-mono"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Loan Balance</Label>
              <Input
                type="number"
                value={formData.loan_balance}
                onChange={(e) => setFormData({ ...formData, loan_balance: parseFloat(e.target.value) || 0 })}
                className="bg-slate-950 border-slate-800 text-white font-mono"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">Monthly Rent</Label>
                <Input
                  type="number"
                  value={formData.monthly_rent}
                  onChange={(e) => setFormData({ ...formData, monthly_rent: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-950 border-slate-800 text-white font-mono"
                />
              </div>
              <div>
                <Label className="text-slate-300">Expenses</Label>
                <Input
                  type="number"
                  value={formData.monthly_expenses}
                  onChange={(e) => setFormData({ ...formData, monthly_expenses: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-950 border-slate-800 text-white font-mono"
                />
              </div>
              <div>
                <Label className="text-slate-300">Mortgage</Label>
                <Input
                  type="number"
                  value={formData.monthly_mortgage}
                  onChange={(e) => setFormData({ ...formData, monthly_mortgage: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-950 border-slate-800 text-white font-mono"
                />
              </div>
            </div>
            <Button 
              onClick={handleSaveProperty}
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            >
              <Save className="h-4 w-4 mr-2" />
              {editingProperty ? 'Update Property' : 'Add Property'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioTracker;
